import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';
import { AuthService, User } from '../services/auth.service';

@Injectable({
  providedIn: 'root'
})
export class OwnerGuard implements CanActivate {
  constructor(
    private readonly authService: AuthService,
    private readonly router: Router
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    return combineLatest([
      this.authService.isLoading$,
      this.authService.isAuthenticated$
    ]).pipe(
      filter(([isLoading]) => !isLoading),
      take(1),
      switchMap(([, isAuthenticated]) => {
        if (!isAuthenticated) {
          return of(
            this.router.createUrlTree(['/login'], {
              queryParams: { returnUrl: state.url }
            })
          );
        }

        const currentUser = this.authService.currentUser;
        if (currentUser?.platformRole === 'owner') {
          return of(this.evaluateUser(currentUser));
        }

        return this.authService.getCurrentUser().pipe(
          map((response) => this.evaluateUser(response.user ?? null)),
          catchError(() => of(this.router.createUrlTree(['/'])))
        );
      })
    );
  }

  private evaluateUser(user: User | null): boolean | UrlTree {
    if (user?.platformRole === 'owner') {
      return true;
    }

    return this.router.createUrlTree(['/']);
  }
}
